// Botón "Ver en mapa" reutilizable: valida las coordenadas y abre un Modal
// con Leaflet centrado en el punto (mismo patrón afterOpenChange +
// destroyOnHidden que MapaUbicacionPicker: el div del mapa recién existe
// cuando el contenido del modal está montado). Sin coordenadas válidas no
// renderiza nada (o el fallback prop).
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Modal, Tooltip, theme } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useT } from '../locales';
import { coordenadasValidas } from '../lib/geo';
import { useLeaflet } from '../lib/useLeaflet';
import { PIN_ANCHOR, PIN_SIZE, pinHtml } from '../lib/pin';
import './ver-mapa.css';

const ZOOM = 15;
const MAX_ZOOM_TILES = 19;
const ALTURA_MAPA = 340;

interface VerMapaButtonProps {
  latitud?: number | string | null;
  longitud?: number | string | null;
  /** Texto del botón (por defecto "Ver en mapa"). */
  label?: string;
  /** Botón solo icono con Tooltip (convención de filas de detalle). */
  iconOnly?: boolean;
  /** Qué renderizar cuando las coordenadas no son válidas (por defecto nada). */
  fallback?: ReactNode;
  /** Estado controlado del modal; si se omite, el componente mantiene su estado interno. */
  open?: boolean;
  /** Notifica cambios de visibilidad cuando el estado del modal es controlado. */
  onOpenChange?: (open: boolean) => void;
  /** Omite el botón visible para abrir el modal desde otra acción accesible. */
  hiddenTrigger?: boolean;
}

export function VerMapaButton({
  latitud,
  longitud,
  label,
  iconOnly = false,
  fallback = null,
  open,
  onOpenChange,
  hiddenTrigger = false,
}: VerMapaButtonProps) {
  const t = useT();
  const { token } = theme.useToken();
  const [internalOpen, setInternalOpen] = useState(false);
  const openState = open ?? internalOpen;
  const L = useLeaflet(openState);
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<import('leaflet').Map | null>(null);

  const ubicacion = coordenadasValidas(latitud, longitud);
  const texto = label ?? t('components.verMapa');

  // Con destroyOnHidden el contenido del modal se monta DESPUÉS de la
  // animación de apertura: afterOpenChange garantiza el momento exacto en
  // que el div del mapa existe. El efecto [openState, L] cubre el caso donde
  // Leaflet aún no había terminado de cargar al abrir; el guard de
  // mapInstanceRef evita la doble inicialización.
  const sincronizarMapa = useCallback(
    (visible: boolean) => {
      if (!visible) {
        mapInstanceRef.current?.remove();
        mapInstanceRef.current = null;
        return;
      }
      if (!L || mapInstanceRef.current || !mapDivRef.current || !ubicacion) return;

      const map = L.map(mapDivRef.current, {
        center: [ubicacion.lat, ubicacion.lng],
        zoom: ZOOM,
        zoomControl: true,
        maxZoom: MAX_ZOOM_TILES,
      });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: MAX_ZOOM_TILES,
      }).addTo(map);

      const icon = L.divIcon({
        className: '',
        html: pinHtml(token.colorPrimary),
        iconSize: PIN_SIZE,
        iconAnchor: PIN_ANCHOR,
      });
      const popupTexto = t('components.verMapaCoords', {
        lat: ubicacion.lat.toFixed(5),
        lng: ubicacion.lng.toFixed(5),
      });
      L.marker([ubicacion.lat, ubicacion.lng], { icon })
        .bindPopup(popupTexto, { offset: L.point(0, -8) })
        .addTo(map);

      mapInstanceRef.current = map;
    },
    [L, ubicacion, token.colorPrimary, t],
  );

  useEffect(() => {
    if (openState && L) sincronizarMapa(true);
    // sincronizarMapa cambia de identidad solo cuando Leaflet o el color
    // primario cambian; la sincronización real la dispara afterOpenChange
    // del Modal (el div del mapa recién existe cuando hay contenido).
  }, [openState, L, sincronizarMapa]);

  if (!ubicacion) return fallback;

  const actualizarOpen = (nextOpen: boolean) => {
    if (open === undefined) setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const cerrar = () => actualizarOpen(false);

  const boton = hiddenTrigger ? null : iconOnly ? (
    <Tooltip title={texto}>
      <Button
        type="text"
        icon={<EnvironmentOutlined />}
        aria-label={texto}
        onClick={() => actualizarOpen(true)}
      />
    </Tooltip>
  ) : (
    <Button type="text" icon={<EnvironmentOutlined />} onClick={() => actualizarOpen(true)}>
      {texto}
    </Button>
  );

  return (
    <>
      {boton}
      <Modal
        title={t('components.verMapaModalTitle')}
        open={openState}
        onCancel={cerrar}
        afterOpenChange={sincronizarMapa}
        destroyOnHidden
        centered
        width={720}
        footer={<Button onClick={cerrar}>{t('common.close')}</Button>}
      >
        <div
          ref={mapDivRef}
          data-testid="ver-mapa"
          className="ver-mapa"
          style={{
            height: ALTURA_MAPA,
            width: '100%',
            borderRadius: 8,
            // Confina los panes internos de Leaflet (z-index 200-1000) al modal.
            isolation: 'isolate',
            // Tokens del tema para popup y controles de Leaflet (CSS vars).
            ['--mapa-popup-bg' as string]: token.colorBgElevated,
            ['--mapa-popup-text' as string]: token.colorText,
            ['--mapa-popup-border' as string]: token.colorBorderSecondary,
            ['--ver-mapa-control-bg' as string]: `${token.colorBgElevated}f2`,
            ['--ver-mapa-control-text' as string]: token.colorTextSecondary,
            ['--ver-mapa-control-border' as string]: token.colorBorderSecondary,
            ['--ver-mapa-control-hover-bg' as string]: token.colorFillSecondary,
          }}
        />
      </Modal>
    </>
  );
}
